// src/components/Trans/SimpleTrans.jsx
// Simplified Trans component for handling the specific pattern we need

import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';

/**
 * Simplified Trans component specifically for the pattern:
 * "<0>{numPar}</0> {numPar, plural, =1 {Person} other {People}} in this conversation"
 */
const SimpleTrans = ({
  i18nKey,
  defaults,
  components = [],
  values = {},
}) => {
  const { t } = useI18n();
  
  // Get the raw translated string
  let text = t(i18nKey, {}, defaults);
  
  if (typeof text !== 'string') {
    return text;
  }
  
  // Handle pluralization FIRST - before variable substitution
  // Look for {numPar, plural, =1 {Person} other {People}}
  text = text.replace(/\{(\w+), plural, =1 \{([^}]+)\} other \{([^}]+)\}\}/g, (match, variable, singular, plural) => {
    const value = values[variable];
    return value === 1 ? singular : plural;
  });
  
  // THEN handle variable substitution - replace {numPar} with actual value
  Object.keys(values).forEach(key => {
    const regex = new RegExp(`\\{${key}\\}`, 'g');
    text = text.replace(regex, String(values[key]));
  });
  
  // Now handle component tags <0>content</0>
  // For the pattern: "<0>0</0> People in this conversation"
  
  const componentRegex = /<(\d+)>([^<]*)<\/\1>/g;
  
  // Replace component tags with React elements
  const processedText = text.replace(componentRegex, (match, componentIndex, content) => {
    const index = parseInt(componentIndex, 10);
    if (components[index]) {
      // We'll return a placeholder that we'll replace with the actual component
      return `__COMPONENT_${index}_${content}__`;
    }
    return content;
  });
  
  // Now split by our placeholders and create the final result
  const parts = [];
  
  // Find all component placeholders
  const placeholderRegex = /__COMPONENT_(\d+)_([^_]*)__/g;
  let lastIndex = 0;
  let match;
  
  while ((match = placeholderRegex.exec(processedText)) !== null) {
    // Add text before this component
    if (match.index > lastIndex) {
      parts.push(processedText.slice(lastIndex, match.index));
    }
    
    const componentIndex = parseInt(match[1], 10);
    const content = match[2];
    
    if (components[componentIndex]) {
      const component = components[componentIndex];
      const Component = component.type || 'span';
      const props = component.props || {};
      
      parts.push(
        <Component key={`comp-${componentIndex}-${parts.length}`} {...props}>
          {content}
        </Component>,
      );
    } else {
      parts.push(content);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add any remaining text
  if (lastIndex < processedText.length) {
    parts.push(processedText.slice(lastIndex));
  }
  
  // If no components were processed, return the original text
  if (parts.length === 0) {
    return text;
  }
  
  return <>{parts}</>;
};

SimpleTrans.propTypes = {
  i18nKey: PropTypes.string.isRequired,
  defaults: PropTypes.string,
  components: PropTypes.arrayOf(PropTypes.element),
  values: PropTypes.object,
};

export default SimpleTrans;
